import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
    computed,
    OnInit,
} from '@angular/core';
import { DocumentModel } from '@fi-app/document/document.model';
import { DocumentService } from '@fi-app/document/document.service';
import { DocumentViewService } from '@fi-app/document/document-view-service/document-view.service';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { isDefined } from '@fi-app/shared/utils';
import { FiOnboardingStepCompletionDialogState } from '../../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

@Component({
    selector: 'fi-document-completion',
    templateUrl: './document-completion.component.html',
    styleUrl: './document-completion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-completion' },
    standalone: false,
})
export class FiDocumentCompletionComponent implements OnInit {
    public isTimerHidden = input<boolean>(false);
    public setInProgress = output<void>();
    public failedToLoadElement = output<void>();
    public saveTempProgress = output<void>();

    private readonly documentService = inject(DocumentService);
    private readonly documentViewService = inject(DocumentViewService);
    private readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    protected loading = signal(false);
    protected templateLayout = signal<TemplateLayoutModel | undefined>(undefined);
    protected hasEditAccessToDocument = signal(false);
    protected languageId = signal<number | undefined>(undefined);
    protected documentContentVisible = computed(() => isDefined(this.templateLayout()) && isDefined(this.languageId()));

    protected readonly timerValueMS: number = 15000;

    public ngOnInit(): void {
        this.loadDocument();
    }

    private loadDocument(): void {
        const documentId = this.completionDialogState.stepCompletionConfig.onboardingStep.ObjectId;
        this.loading.set(true);
        this.documentService
            .GetDocument(documentId)
            .then((document) => {
                this.getDocumentViewData(document);
            })
            .catch((error) => {
                console.error('error', error);
                this.failedToLoadElement.emit();
            });
    }

    private getDocumentViewData(document: DocumentModel): void {
        this.documentViewService
            .prepareEverythingForView(document)
            .then((documentViewData) => {
                this.templateLayout.set(documentViewData.templateLayout);
                this.languageId.set(documentViewData.languageId);
                this.hasEditAccessToDocument.set(documentViewData.hasEditAccess);
            })
            .catch((error) => {
                console.error('error', error);
                this.failedToLoadElement.emit();
            })
            .finally(() => {
                this.loading.set(false);
            });
    }

    protected onLanguageIdChanged(languageId: FiApplicationLanguageType) {
        this.languageId.set(languageId);
    }

    protected onDocumentViewed(): void {
        this.setInProgress.emit();
        this.completionDialogState.setSaveButtonDisabled(false);
        this.saveTempProgress.emit();
    }
}
